import { useState } from 'react';
import { Alert, Button, Card, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  createTicket,
  getTicketClasses,
  listTickets,
  TICKET_PAGE_SIZE,
  type PATicket,
  type PATicketClasses,
} from '../api/tickets';
import { itemsOf, type PAList } from '../api/money';
import { formatInstant } from '../format';
import { DataTable, type Column } from '../money/DataTable';
import { QueryFrame } from '../money/MoneyPage';
import { Pager } from '../money/Pager';
import { usePAQuery } from '../money/usePAQuery';
import { ticketOriginLabel } from '../money/labels';
import { errorText } from '../session/errorText';
import { routePath, type Navigation } from '../router';

/**
 * ============================================================================
 * «ТЕХНИЧЕСКАЯ ПОДДЕРЖКА» — the ticket list and «Новый запрос» (§26, §26a, F26)
 * ============================================================================
 *
 * ⚠ THIS SECTION IS NOT SCOPED BY CONTRACT, and it is the only authenticated section of this
 * portal that is not. A ticket hangs off `ticket.subscriber_id` and §2.16 has no contract in
 * it, so there is no picker here: a two-contract subscriber would otherwise see his own
 * tickets split between two lists for no reason. That is why this screen does not use
 * `MoneyPage`.
 *
 * # The four rules
 *
 *  1. **THE CLASS IS REQUIRED WHEN THE OPERATOR HAS CONFIGURED ANY, AND ABSENT WHEN HE HAS
 *     NOT.** `class_id` is optional on the wire precisely so that an installation with no
 *     live class can still open a ticket — and an id that is not a LIVE class is REFUSED
 *     (`pa.ticket_class_unknown`), never dropped to «no class», because a subscriber who
 *     chose «Ошибка» and got an unclassified ticket is routed to the wrong queue and nothing
 *     tells either party. So: no classes ⇒ no picker at all and `class_id: null`; classes ⇒
 *     a picker the form will not send without.
 *  2. **THE BODY IS REQUIRED**, and that is this SURFACE's rule rather than the domain's:
 *     `tickets.Create` accepts an empty body and posts no opening message (right for a staff
 *     intake typed from a telephone call), and a subscriber-raised ticket with an empty thread
 *     is one nobody can answer. The server refuses it too
 *     (`400 pa.ticket_body_required`); refusing here as well spares him a round trip for a
 *     mistake he can see.
 *  3. **NO FILE PICKER ON THIS FORM — and that is a WIRE limit, not a preference.** §26a
 *     draws one, and the two facts it would need are published ONLY on `PATicketDetail`:
 *     `attachments_available` (false on an installation with no blob storage, where a picker's
 *     Send could only answer 503) and `attachment_max_bytes` (the ceiling the form is
 *     supposed to state instead of guessing). Neither is on the list read or on
 *     `/pa/tickets/classes`, so a picker here would either be a control this build cannot
 *     know the API can honour, or a ceiling invented in the browser. The thread — which
 *     opens IMMEDIATELY after Send — carries both facts and both attach routes, so the
 *     screenshot is one press away and every control on it is one the server admitted to.
 *     Reported as a wire gap rather than papered over.
 *  4. **THE NEW TICKET OPENS, AND THE THREAD IS THE RECEIPT.** The screen navigates to
 *     `/tickets/{id}`, where his own subject and his own words are read back from the server —
 *     and where the file picker is. There is deliberately NO «запрос отправлен» banner: a
 *     banner set here would be set on the screen this act LEAVES, so it would never render at
 *     all. (It was the first shape, and the arm that caught it is «a created ticket … opens its
 *     thread» — an unfalsifiable confirmation is worse than none.)
 *
 * # What is deliberately not here
 *
 *   - **«База знаний»** (§27, one of §26's three cards). POST-LAUNCH: no knowledge-base
 *     entity exists in code or schema, so the card would open nothing.
 *   - **A STATUS control.** §26b draws the status as «dropdown (display)» and no status
 *     crosses this wire in either direction.
 *   - **A RESPONSIBLE or a PERSONAL MANAGER column.** Neither is on the wire: a staff
 *     member's name on an internet-facing listener, and neither is something he acts on.
 *   - **An UNREAD counter.** `ticket_read` is staff-only, so for a subscriber it is
 *     permanently 0 — a badge that could never light.
 */
export function TicketsScreen({ navigate }: { navigate: Navigation['navigate'] }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const { query, reload } = usePAQuery<PAList<PATicket>>(() => listTickets(offset), [offset]);
  /*
    The class dictionary is its OWN read and its failure is the FORM's failure, not the
    list's: a subscriber whose classes could not be loaded must still be able to read the
    tickets he already has. So it is rendered inside the create panel's frame and nowhere
    else — and the panel is what refuses to open without it.
  */
  const classes = usePAQuery<PATicketClasses>(() => getTicketClasses(), []);

  const [creating, setCreating] = useState(false);

  /*
    ⚠ TWO REFUSALS CLOSE THE WHOLE SECTION, AND THEY MUST ALSO CLOSE «Новый запрос».
    `409 pa.tickets_disabled` (the operator switched the section off) and
    `503 pa.tickets_unavailable` (no helpdesk port is wired) are answered by EVERY route in
    this domain, so with the list refused for either of them a create button could only
    produce the same refusal — a dead control, and on the very screen a subscriber reaches
    when he already believes something is broken. Any OTHER failure leaves it: a transient
    read error says nothing about whether a write would land.

    ⚠ AND THE NAV CANNOT DO THIS FOR US, which is worth recording rather than rediscovering:
    `personal_area.tickets_enabled` is NOT among the seven keys `GET /pa/config` publishes, so
    the strip has no way to know the section is off and the entry stays. The screen answering
    with the server's own sentence is the honest floor. Reported as a wire gap.
  */
  const closed = query.status === 'error' && isSectionClosed(query.error);

  return (
    <>
      <h1 className="pa-page-title">{t('tickets.title')}</h1>
      <p className="pa-page-lead">{t('tickets.lead')}</p>

      {creating ? (
        <Card className="pa-block" title={t('tickets.newTitle')}>
          <QueryFrame query={classes.query} reload={classes.reload}>
            {(dictionary) => (
              <NewTicketForm
                classes={dictionary}
                onCancel={() => setCreating(false)}
                onCreated={(id) => {
                  setCreating(false);
                  // The list behind us is now one row short of the truth; it is re-read
                  // rather than patched, because the server assigns the status and the
                  // timestamps.
                  reload();
                  navigate({ kind: 'ticket', id });
                }}
              />
            )}
          </QueryFrame>
        </Card>
      ) : closed ? null : (
        <div className="pa-actions">
          <Button type="primary" size="large" block onClick={() => setCreating(true)}>
            {t('tickets.new')}
          </Button>
        </div>
      )}

      <QueryFrame query={query} reload={reload}>
        {(list) => (
          <>
            <TicketList rows={itemsOf(list)} navigate={navigate} />
            <Pager
              offset={offset}
              pageSize={TICKET_PAGE_SIZE}
              shown={itemsOf(list).length}
              total={list.total}
              onOffset={setOffset}
            />
          </>
        )}
      </QueryFrame>
    </>
  );
}

/**
 * isSectionClosed reports the two refusals that mean «this section is not open on this
 * installation», as opposed to «this read did not work».
 */
function isSectionClosed(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code ?? '';
  return code === 'pa.tickets_disabled' || code === 'pa.tickets_unavailable';
}

/** The list itself — one card per row below 720 px, a grid above it (see `DataTable`). */
function TicketList({
  rows,
  navigate,
}: {
  rows: readonly PATicket[];
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();

  const columns: ReadonlyArray<Column<PATicket>> = [
    {
      key: 'id',
      labelKey: 'tickets.columnID',
      track: 'minmax(0, 0.6fr)',
      render: (row) => <span className="mono">{row.id}</span>,
    },
    {
      key: 'subject',
      labelKey: 'tickets.columnSubject',
      track: 'minmax(0, 2.4fr)',
      // A REAL anchor with the thread's own path: a long-press «Открыть в новой вкладке» and
      // a copied address both work, and the click is intercepted only to avoid a full reload.
      render: (row) => (
        <a
          href={routePath({ kind: 'ticket', id: row.id })}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            navigate({ kind: 'ticket', id: row.id });
          }}
        >
          {row.subject}
        </a>
      ),
    },
    {
      key: 'class',
      labelKey: 'tickets.columnClass',
      // «—» and not a blank: an unclassified ticket is a real state (an installation with no
      // live class, or one archived since), and an empty cell reads as a missing value.
      render: (row) => row.class?.name ?? '—',
    },
    {
      key: 'status',
      labelKey: 'tickets.columnStatus',
      // ⚠ THE STATUS NAME AND ITS TINT ARE THE OPERATOR'S OWN, from the dictionary. The
      // portal invents no palette for a status somebody renamed — and no «done» styling from
      // `is_done` either, because the operator's colour already says it.
      render: (row) => (
        <span className="pa-status-badge" style={row.status.color ? { color: row.status.color } : undefined}>
          {row.status.name}
        </span>
      ),
    },
    {
      key: 'origin',
      labelKey: 'tickets.columnOrigin',
      render: (row) => ticketOriginLabel(row.origin, t),
    },
    {
      key: 'updated',
      labelKey: 'tickets.columnUpdated',
      render: (row) => formatInstant(row.updated_at),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      emptyKey="tickets.empty"
    />
  );
}

/** §26a's form: a class when there is one, a subject, a message. See rules 1–3. */
function NewTicketForm({
  classes,
  onCancel,
  onCreated,
}: {
  classes: PATicketClasses;
  onCancel: () => void;
  onCreated: (id: number) => void;
}) {
  const { t } = useTranslation();
  const options = classes.items ?? [];
  const [classID, setClassID] = useState<number | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [localError, setLocalError] = useState('');

  const send = async () => {
    const trimmedSubject = subject.trim();
    const trimmedBody = body.trim();
    // The three local refusals, each for a mistake he can SEE — a round trip for one of
    // those reads as a fault rather than as «заполните поле». The server refuses all three
    // too, which is what makes these safe rather than authoritative.
    if (options.length > 0 && classID === null) {
      setLocalError(t('tickets.classRequired'));
      return;
    }
    if (trimmedSubject === '') {
      setLocalError(t('tickets.subjectRequired'));
      return;
    }
    if (trimmedBody === '') {
      setLocalError(t('tickets.bodyRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    setLocalError('');
    try {
      const detail = await createTicket({
        class_id: classID,
        subject: trimmedSubject,
        body: trimmedBody,
      });
      onCreated(detail.id);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 16 }} />
      ) : null}

      {/* Rendered only when the operator has configured a class — see rule 1. */}
      {options.length > 0 ? (
        <div className="pa-inline-form">
          <label className="pa-field-label" htmlFor="pa-ticket-class">
            {t('tickets.classLabel')}
          </label>
          <Select<number>
            id="pa-ticket-class"
            className="pa-picker-select"
            size="large"
            value={classID ?? undefined}
            placeholder={t('tickets.classPlaceholder')}
            onChange={(next) => {
              setLocalError('');
              setClassID(next);
            }}
            options={options.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
      ) : null}

      <div className="pa-inline-form">
        <label className="pa-field-label" htmlFor="pa-ticket-subject">
          {t('tickets.subjectLabel')}
        </label>
        <Input
          id="pa-ticket-subject"
          size="large"
          value={subject}
          placeholder={t('tickets.subjectPlaceholder')}
          onChange={(e) => {
            setLocalError('');
            setSubject(e.target.value);
          }}
        />
      </div>

      <div className="pa-inline-form">
        <label className="pa-field-label" htmlFor="pa-ticket-body">
          {t('tickets.bodyLabel')}
        </label>
        <Input.TextArea
          id="pa-ticket-body"
          rows={6}
          value={body}
          placeholder={t('tickets.bodyPlaceholder')}
          onChange={(e) => {
            setLocalError('');
            setBody(e.target.value);
          }}
        />
      </div>

      {localError ? <p className="pa-field-error">{localError}</p> : null}
      {/* Rule 3, said once: the picker is on the thread, which opens next. */}
      <p className="pa-field-hint">{t('tickets.attachLater')}</p>

      <div className="pa-actions">
        <Button type="primary" size="large" block loading={busy} onClick={() => void send()}>
          {t('tickets.send')}
        </Button>
        <Button size="large" block onClick={onCancel}>
          {t('tickets.cancel')}
        </Button>
      </div>
    </>
  );
}
